 
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import Popover from "@insite/mobius/Popover";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { MainNavigationStyles, MappedLink } from "@tSurya/Overrides/Widgets/Header/MainNavigation";
import { COLORS, ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
// import { Typography } from "@tSurya/theme/styleguide";
import * as React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement | null>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
    id: string;
}

interface State {
    hover?: boolean;
    menuItemImage?: string;
}

type Props = ThemeProps<BaseTheme> & OwnProps & State;

class MainNavigationItem extends React.Component<Props> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    constructor(props: ThemeProps<BaseTheme> & OwnProps) {
        super(props);

        this.state = {
            hover: false,
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }
    }
    componentDidMount() {
        window.addEventListener("scroll", this.closePopover, true);
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    hoverOn = () => {
        this.setState({ hover: true });
    };
    hoverOff = () => {
        this.setState({ hover: false });
    };

    handleTriggerClick: React.MouseEventHandler<HTMLElement> = event => {
        const { link } = this.props;
        if (!link.disableNavigation) {
            return;
        }

        event.preventDefault();

        if (link.childrenType === "MegaMenu") {
            this.openPopover();
        }
    };

    render() {
        const {
            id,
            styles,
            link,
            index,
            isOpen,
            menuItemImage,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;
        // const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading =
            hasChildren && (link.childrenType === "CascadingMenu" || link.childrenType === "CustomLinkList");
        const isMega = hasChildren && link.childrenType === "MegaMenu";
        const imageNumber =
            link?.customMenuImageTittle && link?.customMenuImageTittle?.length > 0
                ? isThemeActive(THEME_IDS.MGBW) && link.customMenuImageTittle.length > 1
                    ? 8
                    : 12 - link.customMenuImageTittle.length * 3
                : 12;
        const filteredChildren = link.children?.filter(child => !child.excludeFromNavigation) || [];
        const megaMenuChildWidth = isThemeActive(THEME_IDS.MGBW) && link?.customMenuImageTittle && link.customMenuImageTittle.length > 1 && filteredChildren.length > 0
            ? Math.floor(12 / filteredChildren.length) as GridWidths
            : 12 as GridWidths;
        let menuLink = (
            <Link
                className={`menu-item ${link.dropDownValue}`}
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.disableNavigation ? undefined : link.url}
                aria-disabled={link.disableNavigation || undefined}
                onClick={this.handleTriggerClick}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link?.isBold}
                {link.title}
                {isCascading}
            </Link>
        );
        if (isMega) {
            menuLink = (
                <Link
                    onClick={link.disableNavigation ? this.handleTriggerClick : this.closePopover}
                    className={`menu-item megaMenuItem ${link.dropDownValue}`}
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                    href={link.disableNavigation ? undefined : link.url}
                    role="button"
                    aria-disabled={link.disableNavigation || undefined}
                >
                    {link.title}
                    {isCascading || isMega}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            menuItem = (
                <Menu
                    className={`cascading-menu ${link.dropDownValue}`}
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
            );
        }

        if (isMega) {
            menuItem = (
                <Popover
                    className="mega-menu"
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        _width: 1140,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: React.RefObject<HTMLUListElement | null>) => {
                        if (this.props.container.current?.offsetParent) {
                            const parentPosition = this.props.container.current.getBoundingClientRect();
                            const rect = element.current?.getBoundingClientRect();
                            const decreaseAmount = 0;
                            const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) - decreaseAmount : 0;
                            const cartWidth = 150;
                            const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                                return (
                                    parentPosition.width + cartWidth <= value &&
                                    parentPosition.width + cartWidth > maxWidths[index - 1]
                                );
                            });

                            const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;

                            return {
                                top,
                                position: "fixed",
                                left: "0px",
                                width: `100%`,
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                >
                    <GridContainer
                        className="mega-menu-grid-container"
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                    >
                        {/* {link.customMenuImage && <GridItem {...styles.megaMenuGridItem}>
                            <LazyImage
                                src={link.customMenuImage}
                                {...styles.menuitemImage}
                            />
                            <LazyImage src={menuItemImage}></LazyImage>
                        </GridItem>} */}
                        <GridItem width={imageNumber as GridWidths}>
                            {link
                                .children!.filter(child => !child.excludeFromNavigation)
                                .map((child, index) => (
                                    <GridItem
                                        className="mega-menu-grid-item"
                                        width={megaMenuChildWidth}
                                        key={index}
                                        {...styles.megaMenuGridItem}
                                        css={isThemeActive(THEME_IDS.MGBW) ? css`
                                            display: block;
                                            .mega-menu-heading-link,
                                            .mega-menu-heading-span {
                                                padding-top: 0;
                                                margin-top: 0;
                                            }
                                            .sectionHeading {
                                                padding-top: 24px;
                                                margin-top: 0 !important;
                                                span {
                                                    margin-top: 0 !important;
                                                }
                                            }
                                        ` : styles.megaMenuGridItem?.css}
                                        as="li"
                                    >
                                        {child.url ? (
                                            <Link
                                                className="mega-menu-heading-link"
                                                {...styles.megaMenuHeading}
                                                href={child.url}
                                                onClick={this.closePopover}
                                            >
                                                {child.title}
                                            </Link>
                                        ) : (
                                            child.title && (
                                                <Typography
                                                    className="mega-menu-heading-span"
                                                    {...styles.megaMenuSpanHeading}
                                                >
                                                    {child.title}
                                                </Typography>
                                            )
                                        )}

                                        {child.children && (
                                            <ul data-test-selector={`linksFor_${child.title}`} {...styles.menuList}>
                                                {child.children
                                                    .filter(grandChild => {
                                                        if (grandChild.excludeFromNavigation) {
                                                            return false;
                                                        }
                                                        if (
                                                            grandChild.type === "ChangeCustomerPage" &&
                                                            !this.props.displayChangeCustomerLink
                                                        ) {
                                                            return false;
                                                        }
                                                        if (grandChild.type === "VmiUsersPage") {
                                                            const currentMode = getCookie(NavigationModeCookieName);
                                                            if (!currentMode || currentMode === "Storefront") {
                                                                return false;
                                                            }
                                                        }
                                                        return true;
                                                    })
                                                    .map((grandChild, index) => (
                                                         
                                                        <li key={index}>
                                                            {grandChild.isDropDownValue === "sectionHeading" ? (
                                                                grandChild.url ? (
                                                                    <Link
                                                                        className={`mega-menu-link sectionHeading`}
                                                                        {...styles.megaMenuLink}
                                                                        onClick={this.closePopover}
                                                                        href={grandChild.url}
                                                                        data-test-selector={grandChild.title}
                                                                    >
                                                                        {grandChild.title}
                                                                    </Link>
                                                                ) : (
                                                                    <Typography
                                                                        className={`mega-menu-link sectionHeading`}
                                                                        {...styles.megaMenuLink}
                                                                        data-test-selector={grandChild.title}
                                                                    >
                                                                        {grandChild.title}
                                                                    </Typography>
                                                                )
                                                            ) : (
                                                                <Link
                                                                    className={`mega-menu-link ${
                                                                        grandChild.isDropDownValue
                                                                    }  ${grandChild.isBold ? "megaMenuLinkBold" : ""}`}
                                                                    {...styles.megaMenuLink}
                                                                    onClick={this.closePopover}
                                                                    href={grandChild.url}
                                                                    data-test-selector={grandChild.title}
                                                                >
                                                                    {grandChild?.isBold}
                                                                    {grandChild.title}
                                                                </Link>
                                                            )}
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </GridItem>
                                ))}
                        </GridItem>

                        {/* <LazyImage
                            src="http://localhost:3000/UserFiles/Rectangle%20709.png"
                            {...styles.menuitemImage}
                        />
                        <LazyImage
                            src="http://localhost:3000/UserFiles/Rectangle%20709.png"
                            {...styles.menuitemImage}
                        /> */}
                        {/* {link.customMenuImage && <GridItem {...styles.megaMenuGridItem}>
                            <LazyImage
                                src={link.customMenuImage}
                                {...styles.menuitemImage}
                            />
                            <LazyImage src={menuItemImage}></LazyImage>
                        </GridItem>} */}
                        {link?.customMenuImageTittle && isThemeActive(THEME_IDS.MGBW) && link.customMenuImageTittle.length > 1 ? (
                            (() => {
                                const sbc = (ELEMENTS as any).shopByCollection || {};
                                return (
                            <GridItem
                                {...styles.megaMenuGridItemImg}
                                width={(12 - imageNumber) as GridWidths}
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    gap: ${sbc.gap || "12px"} !important;
                                    padding: 0 !important;
                                    flex-shrink: 0;
                                    min-width: ${sbc.containerWidth || "380px"};
                                `}
                            >
                                <Typography
                                    className="mega-menu-heading-span shop-by-collection-heading"
                                    {...styles.megaMenuSpanHeading}
                                    css={css`
                                        ${styles.megaMenuSpanHeading?.css}
                                        margin: 0 !important;
                                        padding: 0 !important;
                                    `}
                                >
                                    {link.customMenuTitle || sbc.heading || "SHOP BY COLLECTION"}
                                </Typography>
                                <StyledWrapper
                                    css={css`
                                        display: grid;
                                        grid-template-columns: repeat(3, 1fr);
                                        align-items: flex-start;
                                        row-gap: ${sbc.rowGap || "12px"};
                                        width: 100%;
                                        > div:nth-child(3n+1) { justify-self: start; }
                                        > div:nth-child(3n+2) { justify-self: center; }
                                        > div:nth-child(3n+3) { justify-self: end; }
                                    `}
                                >
                                    {link.customMenuImageTittle!.map((o, i) => (
                                        <StyledWrapper
                                            key={i}
                                            css={css`
                                                display: flex;
                                                flex-direction: column;
                                                align-items: center;
                                                width: ${sbc.tileWidth || "90px"};
                                            `}
                                        >
                                            <Link
                                                href={o.fields.internalImgTitledestination}
                                                onClick={this.closePopover}
                                                css={css`
                                                    > span {
                                                        display: flex;
                                                        flex-direction: column;
                                                        align-items: center;
                                                    }
                                                `}
                                            >
                                                <LazyImage
                                                    src={o.fields.internalImgURL}
                                                    css={css`
                                                        width: ${sbc.imageSize || "90px"};
                                                        height: ${sbc.imageSize || "90px"};
                                                        aspect-ratio: 1/1;
                                                        object-fit: contain;
                                                    `}
                                                />
                                                <Typography
                                                    css={css`
                                                        color: ${getColor("text.main")};
                                                        font-family: ${TYPOGRAPHY.fontFamily.base};
                                                        font-style: normal;
                                                        font-weight: ${TYPOGRAPHY.megaMenuLink.weight};
                                                        font-size: 14px;
                                                        line-height: ${TYPOGRAPHY.megaMenuLink.lineHeight};
                                                        margin-top: 0;
                                                    `}
                                                >
                                                    {o.fields.internalImgTitle}
                                                </Typography>
                                            </Link>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </GridItem>
                                );
                            })()
                        ) : link?.customMenuImageTittle && (
                            <GridItem
                                {...styles.megaMenuGridItemImg}
                                width={
                                    (link?.customMenuImageTittle && link.customMenuImageTittle.length * 3) as GridWidths
                                }
                            >
                                {link?.customMenuImageTittle?.map(o => (
                                    // eslint-disable-next-line react/jsx-key
                                    <StyledWrapper>
                                        <Link
                                            href={o.fields.internalImgTitledestination}
                                            onClick={this.closePopover}
                                            css={css`
                                                > span {
                                                    display: flex;
                                                    flex-direction: column;
                                                    align-items: center;
                                                    flex-direction: column;
                                                }
                                            `}
                                        >
                                            <LazyImage src={o.fields.internalImgURL} {...styles.menuitemImage} />

                                            <Typography
                                                css={css`
                                                    color: ${getColor("text.main")};
                                                    font-family: ${TYPOGRAPHY.fontFamily.base};
                                                    font-style: normal;
                                                    font-weight: ${TYPOGRAPHY.megamenuAccentLink.weight};
                                                    font-size: ${TYPOGRAPHY.size.base};
                                                    line-height: ${TYPOGRAPHY.lineHeight.xbase};
                                                    margin-top: 12px;
                                                `}
                                            >
                                                {o.fields.internalImgTitle}
                                            </Typography>
                                        </Link>
                                    </StyledWrapper>
                                ))}
                            </GridItem>
                        )}
                    </GridContainer>
                </Popover>
            );
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);